// Кандидаты, дубли и аналитика воронки. Формы — зеркало contracts/openapi/bark-hiring.v1.yaml.
//
// Контракт правит только сессия ядра; поле, придуманное на фронте, даёт зелёную сборку и
// пустой экран у человека. Поэтому имена здесь ровно те, что в выгруженной схеме, вплоть до
// подчёркиваний, а не «как удобнее читать».
//
// Две особенности, из-за которых типы выглядят именно так.
//
// 1. **Ответ на заведение кандидата бывает двух видов.** 201 — завели нового человека,
//    200 — нашли того же и прикрепили к нему повторный отклик. Для человека это разные
//    события: во втором случае показывать надо найденную карточку, а не «создано». Признак
//    приезжает полем `repeat`, а не кодом ответа, — код до вызывающего кода не доходит.
// 2. **Деньги — целые копейки.** Ожидания кандидата по оплате приходят
//    `pay_expectation_kopecks`, целым числом, и складывать их на клиенте нельзя ни с чем.

import { request } from '@/shared/api/client';
import { queryString } from '@/shared/api/query';
import type { ListResponse } from '@/shared/api/types';

const BASE = '/api/v1/hiring';

// ── Этапы воронки ─────────────────────────────────────────────────────────────────────────

/**
 * contracts/enums/statuses.yaml, candidate_status. Набор заказчика, подтверждён 29 июля 2026:
 * он считает по этим словам воронку у себя и будет сверять их глазами.
 */
export const CANDIDATE_STATUSES = [
  'new',
  'approved',
  'rejected',
  'waitlist',
  'ticket_bought',
  'no_show',
  'blacklisted',
  'hired',
] as const;

export type CandidateStatus = (typeof CANDIDATE_STATUSES)[number];

export function isCandidateStatus(value: string): value is CandidateStatus {
  return (CANDIDATE_STATUSES as readonly string[]).includes(value);
}

/**
 * Что можно поставить руками. «Оформлен» здесь нет намеренно: его выставляют кадры переводом
 * в сотрудники, и поставить его отдельным действием значило бы отметить человека принятым без
 * карточки сотрудника — без ФИО в реестре, без тарифа и без табеля (тз 5.8.1).
 */
export const SETTABLE_STATUSES = CANDIDATE_STATUSES.filter(
  (code): code is Exclude<CandidateStatus, 'hired'> => code !== 'hired',
);

export const REJECTION_REASONS = [
  'auto_screened',
  'not_qualified',
  'declined_offer',
  'unreachable',
  'blacklist_match',
  'other',
] as const;

export type RejectionReason = (typeof REJECTION_REASONS)[number];

/** Итог сверки по чёрному списку: чисто, точное совпадение, возможное совпадение. */
export type CheckResult = 'clear' | 'exact' | 'possible';

// ── Строка списка и карточка ──────────────────────────────────────────────────────────────

/** Строка воронки. Ровно то, что нужно списку: кто, где, на каком этапе и чей. */
export interface CandidateBrief {
  id: string;
  display_name: string;
  status: CandidateStatus;
  phone_normalized: string | null;
  city: string | null;
  region: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
  blacklist_result: CheckResult | null;
  employee_id: string | null;
  first_seen_at: string | null;
  created_at: string;
  tags?: string[];
}

export interface ContactIn {
  /** Телефон, мессенджер, почта, ссылка на профиль. */
  kind: string;
  value: string;
}

/** Квалификация: то, что выясняет помощник по сценарию либо рекрутёр руками. */
export interface Qualification {
  vahta_ready?: boolean | null;
  vahta_length_days?: number | null;
  license_present?: boolean | null;
  /** Разряд: 4, 5, 6. Строкой — так его отдаёт сервер. */
  license_grade?: string | null;
  region_ready?: string | null;
  site_wanted?: string | null;
  schedule_wanted?: string | null;
  /** Ожидания по оплате целым числом копеек. */
  pay_expectation_kopecks?: number | null;
}

export interface CandidateApplicationOut {
  id: string;
  account_id: string | null;
  external_id: string;
  vacancy_external_id: string | null;
  external_chat_id: string | null;
  /** Повторный отклик того же человека: второй карточки при нём не заводится (тз 5.8.1). */
  is_repeat: boolean;
  /** Написал или посмотрел номер. */
  kind: string | null;
  received_at: string | null;
  created_at: string;
}

export interface NoteOut {
  id: string;
  body: string;
  author_user_id: string | null;
  author_name: string | null;
  created_at: string;
}

export interface CandidateStatusHistoryOut {
  id: string;
  candidate_id: string;
  status_from: CandidateStatus | null;
  status_to: CandidateStatus;
  reason: string | null;
  changed_by: string | null;
  changed_by_name: string | null;
  created_at: string;
}

export interface CandidateDuplicateHit {
  candidate_id: string;
  display_name: string;
  phone_normalized: string | null;
  city: string | null;
  status: CandidateStatus;
  /** По чему совпало: точно по телефону или спорно по ФИО. */
  matched_by: 'phone' | 'fuzzy';
  score: number;
  signals?: string[];
}

export interface CandidateDuplicateReport {
  found: boolean;
  hits?: CandidateDuplicateHit[];
}

/** Карточка целиком — состав из тз 5.8.1, перечисленный там дословно. */
export interface CandidateOut extends CandidateBrief {
  last_name: string | null;
  first_name: string | null;
  middle_name: string | null;
  birth_date: string | null;
  phone_raw: string | null;
  guard_license_no: string | null;
  account_id: string | null;
  source_vacancy_id: string | null;
  source_recruiter_user_id: string | null;
  source_recruiter_name: string | null;
  rejection_reason: RejectionReason | null;
  rejection_note: string | null;
  blacklist_checked_at: string | null;
  application_file_id: string | null;
  hired_at: string | null;
  merged_into_id: string | null;
  merged_from?: string[];
  extra_contacts?: ContactIn[];
  qualification?: Qualification | null;
  applications?: CandidateApplicationOut[];
  notes?: NoteOut[];
  status_history?: CandidateStatusHistoryOut[];
  dialog_id?: string | null;
  dialog_status?: string | null;
  /** Приезжает один раз, вместе с только что заведённой карточкой. */
  duplicate_warning?: CandidateDuplicateReport | null;
  /** true — отклик прикрепился к найденной карточке, новой не появилось. */
  repeat?: boolean;
}

export interface CandidatesQuery {
  status?: string;
  owner_user_id?: string;
  city?: string;
  tag?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface CandidateIn {
  last_name?: string | null;
  first_name?: string | null;
  middle_name?: string | null;
  birth_date?: string | null;
  /** В любом виде: нормализует одна функция на весь проект, на сервере. */
  phone?: string | null;
  city?: string | null;
  region?: string | null;
  guard_license_no?: string | null;
  owner_user_id?: string | null;
  source_recruiter_user_id?: string | null;
  source_vacancy_id?: string | null;
  tags?: string[];
  extra_contacts?: ContactIn[];
  qualification?: Qualification | null;
}

export type CandidatePatch = Omit<
  CandidateIn,
  'tags' | 'source_recruiter_user_id' | 'source_vacancy_id'
>;

export interface CandidateStatusChangeIn {
  status: Exclude<CandidateStatus, 'hired'>;
  /** Причина словами, попадает в историю. */
  reason?: string | null;
  /** Справочная причина отказа: обязательна при «не согласован». */
  rejection_reason?: RejectionReason | null;
}

export interface PromoteIn {
  employment_type_id?: string | null;
  hired_at?: string | null;
  /** Заявление о приёме на работу; можно приложить заранее. */
  application_file_id?: string | null;
  /** Подтверждение кадров для оформления без заявления. */
  hr_confirmation?: string | null;
}

export interface PromoteOut {
  candidate: CandidateOut;
  employee_id: string;
  /** Повтор той же попытки: вернулся ТОТ ЖЕ сотрудник, второго не завелось. */
  replay: boolean;
}

/** Факт применения компенсации билета ЯДРОМ. Найм его только показывает. */
export interface TicketCompensationOut {
  employee_id: string;
  candidate_id?: string | null;
  amount_kopecks: number;
  applied_at: string;
  applied_by?: string;
  note?: string;
}

export function listCandidates(
  params: CandidatesQuery,
  signal?: AbortSignal,
): Promise<ListResponse<CandidateBrief>> {
  return request(`${BASE}/candidates${queryString({ ...params })}`, { signal });
}

export function getCandidate(id: string, signal?: AbortSignal): Promise<CandidateOut> {
  return request(`${BASE}/candidates/${id}`, { signal });
}

export function createCandidate(body: CandidateIn, idempotencyKey: string): Promise<CandidateOut> {
  return request(`${BASE}/candidates`, { method: 'POST', body, idempotencyKey });
}

export function patchCandidate(id: string, body: CandidatePatch): Promise<CandidateOut> {
  return request(`${BASE}/candidates/${id}`, { method: 'PATCH', body });
}

export function changeCandidateStatus(
  id: string,
  body: CandidateStatusChangeIn,
): Promise<CandidateOut> {
  return request(`${BASE}/candidates/${id}/status`, { method: 'POST', body });
}

export function listStatusHistory(
  id: string,
  signal?: AbortSignal,
): Promise<ListResponse<CandidateStatusHistoryOut>> {
  return request(`${BASE}/candidates/${id}/status-history`, { signal });
}

export function addNote(id: string, body: string): Promise<NoteOut> {
  return request(`${BASE}/candidates/${id}/notes`, { method: 'POST', body: { body } });
}

export function addTags(id: string, tags: string[]): Promise<CandidateOut> {
  return request(`${BASE}/candidates/${id}/tags`, { method: 'POST', body: { tags } });
}

export function promoteCandidate(
  id: string,
  body: PromoteIn,
  idempotencyKey: string,
): Promise<PromoteOut> {
  return request(`${BASE}/candidates/${id}/promote`, { method: 'POST', body, idempotencyKey });
}

export function fetchTicketCompensation(
  id: string,
  signal?: AbortSignal,
): Promise<TicketCompensationOut> {
  return request(`${BASE}/candidates/${id}/ticket-compensation`, { signal });
}

// ── Дубли ─────────────────────────────────────────────────────────────────────────────────

export type DuplicateReviewStatus = 'pending' | 'merged' | 'separate';

export interface DuplicateReviewOut {
  id: string;
  left: CandidateBrief;
  right: CandidateBrief;
  score: number | null;
  signals?: string[];
  status: DuplicateReviewStatus;
  decided_by: string | null;
  decided_by_name: string | null;
  decided_at: string | null;
  created_at: string;
}

export interface MergeOut {
  kept: CandidateOut;
  merged_id: string;
  by_rule: 'phone' | 'manual';
  moved_applications: number;
  moved_notes: number;
}

export interface DuplicateSearchQuery {
  phone?: string;
  last_name?: string;
  first_name?: string;
  middle_name?: string;
  birth_date?: string;
  city?: string;
  exclude_id?: string;
}

export function listDuplicateReviews(
  status: string | undefined,
  signal?: AbortSignal,
): Promise<ListResponse<DuplicateReviewOut>> {
  return request(`${BASE}/duplicates${queryString({ status })}`, { signal });
}

export function searchDuplicates(
  params: DuplicateSearchQuery,
  signal?: AbortSignal,
): Promise<CandidateDuplicateReport> {
  return request(`${BASE}/duplicates/search${queryString({ ...params })}`, { signal });
}

export function mergeCandidates(keepId: string, mergeId: string): Promise<MergeOut> {
  return request(`${BASE}/duplicates/merge`, {
    method: 'POST',
    body: { keep_id: keepId, merge_id: mergeId },
  });
}

export function decideDuplicate(
  reviewId: string,
  decision: 'merge' | 'separate',
  keepId?: string,
): Promise<DuplicateReviewOut> {
  return request(`${BASE}/duplicates/${reviewId}/decide`, {
    method: 'POST',
    body: { decision, keep_id: keepId ?? null },
  });
}

// ── Аналитика воронки ─────────────────────────────────────────────────────────────────────

export interface FunnelStageOut {
  status: string;
  status_title: string;
  /** Сколько кандидатов стоит на этапе сейчас. */
  count: number;
  /** Сколько побывало на этапе за период. */
  reached: number;
  /** Сколько висит дольше порога. */
  stuck: number;
  avg_days?: number | null;
  max_days?: number | null;
}

export interface ConversionOut {
  from_status: string;
  from_title: string;
  to_status: string;
  to_title: string;
  reached_from: number;
  reached_to: number;
  /** Доля дошедших, не проценты: округляет интерфейс. Пусто — на исходном этапе никого. */
  rate?: number | null;
}

/** Кто именно застрял. Число без имён — метрика, на которую нельзя нажать. */
export interface StuckCandidateOut {
  candidate_id: string;
  last_name?: string | null;
  first_name?: string | null;
  status: string;
  status_title: string;
  days: number;
  owner_user_id?: string | null;
  owner_name?: string | null;
}

export interface FunnelOut {
  stages: FunnelStageOut[];
  conversions: ConversionOut[];
  stuck_candidates: StuckCandidateOut[];
  stuck_days: number;
  generated_at: string;
  since?: string | null;
  /** Оговорка сервера: числа описывают уже случившееся и роста не обещают. */
  note?: string;
}

export interface RecruiterLoadOut {
  /** Пусто — кандидаты без ответственного. */
  user_id?: string | null;
  user_name?: string | null;
  total: number;
  in_work: number;
  hired: number;
  rejected: number;
  hired_share?: number | null;
  avg_days_to_hire?: number | null;
}

export interface RecruitersOut {
  items: RecruiterLoadOut[];
  total: number;
  generated_at: string;
  since?: string | null;
  note?: string;
}

export function fetchFunnel(
  params: { since?: string; stuck_days?: number },
  signal?: AbortSignal,
): Promise<FunnelOut> {
  return request(`${BASE}/analytics/funnel${queryString({ ...params })}`, { signal });
}

export function fetchRecruiterLoad(
  since: string | undefined,
  signal?: AbortSignal,
): Promise<RecruitersOut> {
  return request(`${BASE}/analytics/recruiters${queryString({ since })}`, { signal });
}
